import { useEffect, useState } from 'react';
import type { StudioId } from '@/lib/studios';

// WHICH TOOLS CAN RUN RIGHT NOW (owner 2026-10-16): the tools that render on
// RunPod (music videos, ads, TikTok story videos, storybooks) show a greyed,
// locked button that says they are temporarily offline for routine
// maintenance whenever the server's check says they cannot run (an empty
// RunPod balance, an endpoint that does not answer), and come back on by
// themselves when it says they can. FaceShot-ChopShop-web/routes/availability.js
// does the check; this asks it once a minute. Until it answers, everything is
// open (the check failing must never lock the site).

// avatars = FaceLock training; render = ChopBox's Animate (and the Telegram studio).
export type GpuTool = 'music' | 'ads' | 'story' | 'storybook' | 'avatars' | 'render';
type Availability = Record<GpuTool, boolean>;

const OPEN: Availability = { music: true, ads: true, story: true, storybook: true, avatars: true, render: true };
const POLL_MS = 60 * 1000;

let current: Availability = OPEN;
const listeners = new Set<(a: Availability) => void>();
let timer: number | undefined;
let inflight = false;

async function refresh() {
  if (inflight) return;
  inflight = true;
  try {
    const r = await fetch('/api/web/availability', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (r.ok) {
      const j = (await r.json()) as { tools?: Partial<Availability> };
      const next = { ...OPEN, ...(j.tools || {}) };
      if (Object.keys(next).some((k) => next[k as GpuTool] !== current[k as GpuTool])) {
        current = next;
        listeners.forEach((fn) => fn(current));
      }
    }
  } catch {
    /* keep the last answer */
  } finally {
    inflight = false;
  }
}

function start() {
  if (timer !== undefined || typeof window === 'undefined') return;
  void refresh();
  timer = window.setInterval(() => void refresh(), POLL_MS);
}

/** The whole map, re-rendering when any tool goes offline or comes back. */
export function useToolAvailability(): Availability {
  const [value, setValue] = useState<Availability>(current);
  useEffect(() => {
    listeners.add(setValue);
    setValue(current);
    start();
    return () => {
      listeners.delete(setValue);
    };
  }, []);
  return value;
}

/** Be told when any tool goes offline or comes back (does not start the check). */
export function onToolAvailabilityChange(fn: (a: Availability) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** The GPU tool behind a studio (the editor and the quick tools run on our own CPU). */
export function gpuToolFor(studio: StudioId | string): GpuTool | null {
  if (studio === 'music' || studio === 'corrido' || studio === 'rap') return 'music';
  if (studio === 'ads') return 'ads';
  if (studio === 'story') return 'story';
  if (studio === 'storybook') return 'storybook';
  return null;
}

/** Is this studio usable right now? */
export function useStudioOnline(studio: StudioId | string): boolean {
  const map = useToolAvailability();
  const tool = gpuToolFor(studio);
  return tool ? map[tool] : true;
}

/** Tests: pretend the server answered. */
export function setToolAvailabilityForTests(next: Partial<Availability> | null) {
  current = next ? { ...OPEN, ...next } : OPEN;
  listeners.forEach((fn) => fn(current));
}
